// Story 4.3 — TanStack Query hook wrapping GET /api/v1/vault/shares/:id.
//
// One cache entry per share_id (the key embeds the id so different
// shares don't share the same entry). Used by ShareDetailPage.tsx.
//
// Invalidated by:
//   * useReveal.onSuccess (Story 3.6 hook EXTENDED in Story 4.3) — the
//     partial-key `["share-detail"]` match invalidates every share-
//     detail cache entry when ANY reveal completes. Useful when a
//     reveal happens inside the welcome flow that subsequently lands
//     on this page.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";
import type { ShareDetailResponse } from "./vaultTypes";

export interface ShareDetailFetchError {
  code: string;
  message: string;
  status: number;
}

async function fetchShareDetail(shareId: string): Promise<ShareDetailResponse> {
  const response = await fetch(`/api/v1/vault/shares/${shareId}`, {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: ShareDetailFetchError = {
      code: "SHARE_DETAIL_FETCH_FAILED",
      message: `Share detail fetch failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          status: response.status,
        };
      }
    } catch {
      // body not JSON
    }
    throw detail;
  }
  return (await response.json()) as ShareDetailResponse;
}

export function shareDetailQueryKey(shareId: string) {
  return ["share-detail", shareId] as const;
}

export function useShareDetail(shareId: string | undefined) {
  return useQuery<ShareDetailResponse, ShareDetailFetchError>({
    queryKey: ["share-detail", shareId ?? ""],
    queryFn: () => fetchShareDetail(shareId!),
    enabled: Boolean(shareId),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: false,
  });
}
